/**
 * @file useChildPortalNavigation.js
 * @description Shared hook for inline child portal navigation with PortalPageView.
 * Manages child portal state and sessionStorage cleanup for navigation stacks.
 * Used by ViewLivePage and PreviewTab.
 */
import { useState, useCallback } from 'react';

const BACK_KEY = 'navigationStack:back';
const FORWARD_KEY = 'navigationStack:forward';
const NAMES_KEY = 'navigationStack:names';

function clearNavigationStorage() {
    try {
        sessionStorage.removeItem(BACK_KEY);
        sessionStorage.removeItem(FORWARD_KEY);
        sessionStorage.removeItem(NAMES_KEY);
    } catch (e) {
        console.warn('Failed to clear navigationStack keys', e);
    }
}

function useChildPortalNavigation({ parentPageId, onClose }) {
    const [childPortalId, setChildPortalId] = useState(null);
    const [navigationStack, setNavigationStack] = useState([]);

    const openChildPortal = useCallback(
        (portalId, navStack) => {
            if (!portalId) return;

            try {
                const backStackRaw = sessionStorage.getItem(BACK_KEY);
                const baseStack = backStackRaw
                    ? JSON.parse(backStackRaw)
                    : parentPageId
                      ? [parentPageId]
                      : [];
                const newBack = [...baseStack, portalId];
                sessionStorage.setItem(BACK_KEY, JSON.stringify(newBack));
                sessionStorage.removeItem(FORWARD_KEY);
                sessionStorage.removeItem(NAMES_KEY);
            } catch (e) {
                console.warn(
                    'Failed to update navigationStack:back in sessionStorage',
                    e,
                );
            }

            setChildPortalId(portalId);
            setNavigationStack(navStack || []);
        },
        [parentPageId],
    );

    const closeChildPortal = useCallback(() => {
        clearNavigationStorage();
        setChildPortalId(null);
        setNavigationStack([]);
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    const isShowingChildPortal = childPortalId !== null;

    return {
        childPortalId,
        setChildPortalId,
        navigationStack,
        isShowingChildPortal,
        openChildPortal,
        closeChildPortal,
        clearNavigationStorage,
    };
}

export { clearNavigationStorage };
export default useChildPortalNavigation;
